import React, { useState, useEffect, useContext } from "react";
import { useNavigate, Link } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { AuthContext } from "../contexts/AuthContext.js";
import "./styles/Signup.css";

let stripePromise = null;

const getStripePromise = async () => {
  if (!stripePromise) {
    const resp = await fetch("/api/config");
    const data = await resp.json();
    if (data.stripe_publishable_key) {
      stripePromise = loadStripe(data.stripe_publishable_key);
    }
  }
  return stripePromise;
};

const formatPrice = (cents) => `$${(cents / 100).toFixed(2)}`;

const StepIndicator = ({ step }) => (
  <div className="signup-steps">
    <div className={`signup-step ${step >= 1 ? "signup-step-active" : ""}`}>
      <span className="signup-step-num">1</span>
      <span className="signup-step-label">Account</span>
    </div>
    <div className="signup-step-line" />
    <div className={`signup-step ${step >= 2 ? "signup-step-active" : ""}`}>
      <span className="signup-step-num">2</span>
      <span className="signup-step-label">Subscribe</span>
    </div>
  </div>
);

const PaymentForm = ({ selectedPlan, accountData, onSuccess, onError, onBack }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setLoading(true);
    setError(null);

    try {
      const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card: elements.getElement(CardElement),
      });

      if (pmError) {
        setError(pmError.message);
        setLoading(false);
        return;
      }

      const resp = await fetch("/api/signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username: accountData.username,
          email: accountData.email,
          password: accountData.password,
          plan_id: selectedPlan.id,
          payment_method_id: paymentMethod.id,
        }),
      });

      const data = await resp.json();

      if (!resp.ok) {
        throw new Error(data.message || "Signup failed");
      }

      if (data.token) {
        onSuccess(data.token);
        return;
      }

      if (data.requires_action && data.client_secret) {
        const { error: confirmError } = await stripe.confirmCardPayment(data.client_secret);
        if (confirmError) {
          setError(confirmError.message);
          setLoading(false);
          return;
        }

        const completeResp = await fetch("/api/signup/complete", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            username: accountData.username,
            email: accountData.email,
            password: accountData.password,
            customer_id: data.customer_id,
            subscription_id: data.subscription_id,
            plan_id: data.plan_id,
          }),
        });

        const completeData = await completeResp.json();
        if (!completeResp.ok) {
          throw new Error(completeData.message || "Failed to complete signup");
        }

        onSuccess(completeData.token);
        return;
      }

      throw new Error("Unexpected response from server");
    } catch (err) {
      setError(err.message);
      onError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const hasSpecial = selectedPlan.special_price_cents && selectedPlan.special_price_cents > 0;
  const displayPrice = hasSpecial ? selectedPlan.special_price_cents : selectedPlan.price_cents;

  return (
    <form onSubmit={handleSubmit} className="signup-payment-form">
      <div className="signup-plan-summary">
        <span className="signup-plan-summary-name">{selectedPlan.name}</span>
        <span className="signup-plan-summary-price">
          {formatPrice(displayPrice)}/{selectedPlan.interval_label.toLowerCase()}
        </span>
      </div>
      <label className="signup-field-label">Card Details</label>
      <div className="signup-card-element">
        <CardElement
          options={{
            style: {
              base: {
                fontSize: "16px",
                color: "#1e293b",
                fontFamily: "'Inter', sans-serif",
                "::placeholder": { color: "#94a3b8" },
              },
              invalid: { color: "#dc2626" },
            },
          }}
        />
      </div>
      {error && <div className="signup-error">{error}</div>}
      <button type="submit" disabled={!stripe || loading} className="signup-btn signup-btn-primary">
        {loading ? "Processing Payment..." : `Pay ${formatPrice(displayPrice)} & Create Account`}
      </button>
      <button type="button" onClick={onBack} className="signup-btn signup-btn-back" disabled={loading}>
        Back
      </button>
      <p className="signup-secure-note">Your payment is securely processed by Stripe.</p>
    </form>
  );
};

const Signup = () => {
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [accountData, setAccountData] = useState({ username: "", email: "", password: "", confirmPassword: "" });
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isFree, setIsFree] = useState(false);
  const [stripeReady, setStripeReady] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [freeLoading, setFreeLoading] = useState(false);

  useEffect(() => {
    const init = async () => {
      try {
        const [plansResp, sp] = await Promise.all([
          fetch("/api/subscription-plans"),
          getStripePromise()
        ]);
        const plansData = await plansResp.json();
        setPlans(plansData);
        setStripeReady(sp);
        if (plansData.length > 0) setSelectedPlan(plansData[0]);
      } catch (err) {
        setError("Failed to load subscription plans");
      }
    };
    init();
  }, []);

  const handleAccountChange = (e) => {
    const { name, value } = e.target;
    setAccountData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAccountSubmit = (e) => {
    e.preventDefault();
    setError(null);

    if (accountData.password !== accountData.confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    if (accountData.password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    setStep(2);
  };

  const handleFreeSignup = async () => {
    setFreeLoading(true);
    setError(null);
    try {
      const resp = await fetch("/api/signup/free", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username: accountData.username,
          email: accountData.email,
          password: accountData.password,
        }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        throw new Error(data.message || "Signup failed");
      }
      setSuccess(true);
      login(data.token);
      setTimeout(() => {
        navigate("/", { replace: true });
      }, 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setFreeLoading(false);
    }
  };

  const handlePaymentSuccess = (token) => {
    setSuccess(true);
    login(token);
    setTimeout(() => {
      navigate("/", { replace: true });
    }, 2000);
  };

  if (success) {
    return (
      <div className="signup-page">
        <div className="signup-card signup-success-card">
          <div className="signup-success-icon">&#10003;</div>
          <h2>Welcome to VyralNews!</h2>
          <p>{isFree ? "Your free account is ready! You can read up to 10 articles per week." : "Your account is set up and your subscription is active. You now have full access to all content."}</p>
          <p>Redirecting to homepage...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="signup-page">
      <div className="signup-card">
        <h2>Create Your Account</h2>
        <StepIndicator step={step} />

        {error && <div className="signup-error">{error}</div>}

        {step === 1 && (
          <form onSubmit={handleAccountSubmit}>
            <div className="signup-form-group">
              <label htmlFor="username">Username</label>
              <input
                id="username"
                name="username"
                type="text"
                value={accountData.username}
                onChange={handleAccountChange}
                placeholder="Choose a username"
                autoComplete="username"
                required
              />
            </div>
            <div className="signup-form-group">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                name="email"
                type="email"
                value={accountData.email}
                onChange={handleAccountChange}
                placeholder="Enter your email"
                autoComplete="email"
                required
              />
            </div>
            <div className="signup-form-group">
              <label htmlFor="password">Password</label>
              <input
                id="password"
                name="password"
                type="password"
                value={accountData.password}
                onChange={handleAccountChange}
                placeholder="At least 6 characters"
                autoComplete="new-password"
                minLength={6}
                required
              />
            </div>
            <div className="signup-form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                value={accountData.confirmPassword}
                onChange={handleAccountChange}
                placeholder="Confirm your password"
                autoComplete="new-password"
                required
              />
            </div>
            <button type="submit" className="signup-btn signup-btn-primary">
              Continue to Plans
            </button>
            <div className="signup-footer">
              <p>Already have an account? <Link to="/login">Sign In</Link></p>
            </div>
          </form>
        )}

        {step === 2 && (
          <div className="signup-step2">
            <p className="signup-step2-intro">Choose a plan to complete your account. View our <Link to="/pricing">Pricing</Link>, <Link to="/payment-policy">Payment Policy</Link>, and <Link to="/refund-policy">Refund Policy</Link>.</p>

            <div className="signup-plans-grid">
              <div
                className={`signup-plan-card ${isFree ? "signup-plan-card-selected" : ""}`}
                onClick={() => { setIsFree(true); setSelectedPlan(null); }}
              >
                <h3>Free</h3>
                <div className="signup-plan-price">
                  <span>$0.00</span>
                </div>
                <p className="signup-plan-special">10 articles per week</p>
              </div>
              {plans.map((plan) => {
                const hasSpecial = plan.special_price_cents && plan.special_price_cents > 0;
                const isSelected = !isFree && selectedPlan?.id === plan.id;
                return (
                  <div
                    key={plan.id}
                    className={`signup-plan-card ${isSelected ? "signup-plan-card-selected" : ""}`}
                    onClick={() => { setIsFree(false); setSelectedPlan(plan); }}
                  >
                    {hasSpecial && <div className="signup-plan-badge">Special</div>}
                    <h3>{plan.name}</h3>
                    <div className="signup-plan-price">
                      {hasSpecial && <span className="signup-plan-price-old">{formatPrice(plan.price_cents)}</span>}
                      <span>{formatPrice(hasSpecial ? plan.special_price_cents : plan.price_cents)}</span>
                      <span className="signup-plan-interval">/{plan.interval_label.toLowerCase()}</span>
                    </div>
                    {hasSpecial && plan.special_label && (
                      <p className="signup-plan-special">{plan.special_label}</p>
                    )}
                    <p className="signup-plan-special">Unlimited articles</p>
                  </div>
                );
              })}
            </div>

            {isFree && (
              <div className="signup-free-section">
                <button
                  type="button"
                  onClick={handleFreeSignup}
                  disabled={freeLoading}
                  className="signup-btn signup-btn-primary"
                >
                  {freeLoading ? "Creating Account..." : "Create Free Account"}
                </button>
                <button type="button" onClick={() => { setStep(1); setError(null); }} className="signup-btn signup-btn-back" disabled={freeLoading}>
                  Back
                </button>
              </div>
            )}

            {!isFree && selectedPlan && stripeReady && (
              <Elements stripe={stripeReady}>
                <PaymentForm
                  selectedPlan={selectedPlan}
                  accountData={accountData}
                  onSuccess={handlePaymentSuccess}
                  onError={setError}
                  onBack={() => { setStep(1); setError(null); }}
                />
              </Elements>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Signup;
